import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Plus, Minus, Flame, Leaf, Baby, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";
import BlurImage from "./BlurImage";
import DishReviewsSection from "./DishReviewsSection";
import DishRatingPill, { useRatings } from "./DishRatings";

/**
 * DishDetailModal — high-end restaurant dish detail sheet.
 *
 * Big edge-to-edge hero image, full description, dietary badges,
 * quantity stepper and a free-text "special instructions" field
 * (e.g. "extra spicy", "no onions") that flows through to the cart's
 * per-line notes column and — via the POS bridge — into the kitchen ticket.
 *
 * Uses a portal so the modal escapes its parent's stacking context and
 * always sits above the sticky order bar / cart drawer.
 */
function priceToNumber(p) {
  return parseFloat((p || "0").replace(/[^\d.]/g, "")) || 0;
}

function inferDietary(item) {
  const text = `${item?.name || ""} ${item?.description || ""}`.toLowerCase();
  const badges = [];
  if (/\bhot\b|\bspicy\b|\bpepper\b|\bchili|\bchilli|habanero|jalape|scotch bonnet/.test(text)) {
    badges.push({ key: "spicy", label: "Spicy", icon: Flame });
  }
  if ((item?.tag || "").toLowerCase() === "kids") {
    badges.push({ key: "kids", label: "Kids friendly", icon: Baby });
  }
  return badges;
}

export default function DishDetailModal({ item, open, onClose }) {
  const { add } = useCart();
  const { refresh: refreshRatings } = useRatings();
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState("");

  // Reset on each open so the modal doesn't remember state from the previous
  // dish (spicy note leaked onto a mild dish would be a bad experience).
  useEffect(() => {
    if (open) { setQty(1); setNotes(""); }
  }, [open, item?.id]);

  // Escape closes the modal + lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open || !item) return null;

  const dietary = inferDietary(item);
  const lookup_key = item.lookup_key;
  const price = priceToNumber(item.price);
  const canAdd = !!lookup_key && !item.sold_out;

  const handleAdd = () => {
    if (!canAdd) return;
    add(
      {
        lookup_key,
        name: item.name,
        price,
        image: item.image,
        notes: notes.trim() || undefined,
      },
      qty
    );
    onClose?.();
  };

  const modal = (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center"
      data-testid="dish-modal"
    >
      {/* Scrim */}
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/75 backdrop-blur-sm"
      />

      {/* Sheet */}
      <div
        className="relative bg-[var(--mm-bg)] border border-[var(--mm-cream)]/12 w-full sm:max-w-[560px] max-h-[92vh] overflow-y-auto animate-[fadeInUp_0.28s_ease-out]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dish-modal-title"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center bg-[var(--mm-bg)]/70 backdrop-blur border border-[var(--mm-cream)]/15 text-[var(--mm-cream)] hover:bg-[var(--mm-accent)] hover:text-[var(--mm-bg)] transition-colors"
          data-testid="dish-modal-close"
        >
          <X size={16} />
        </button>

        {/* Hero image */}
        {item.image ? (
          <BlurImage
            src={item.image}
            alt={item.name}
            className="w-full h-64 sm:h-80"
          />
        ) : (
          <div className="w-full h-64 sm:h-80 bg-gradient-to-br from-[var(--mm-accent)]/20 to-[var(--mm-bg)] flex items-center justify-center">
            <div className="font-display italic text-5xl text-[var(--mm-accent)]">
              {item.name?.split(" ")[0]}
            </div>
          </div>
        )}

        <div className="p-6 sm:p-8">
          <p className="section-label mb-3">{item.tag}</p>
          <div className="flex items-start justify-between gap-4 mb-2">
            <h2
              id="dish-modal-title"
              className="font-display italic text-3xl sm:text-4xl text-[var(--mm-cream)] leading-tight"
            >
              {item.name}
            </h2>
            <span className="font-display text-2xl text-[var(--mm-accent)] shrink-0">
              {item.price}
            </span>
          </div>
          {lookup_key && (
            <div className="mb-4">
              <DishRatingPill lookup_key={lookup_key} size="lg" />
            </div>
          )}

          {dietary.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-5" data-testid="dish-modal-badges">
              {dietary.map(({ key, label, icon: Icon }) => (
                <span
                  key={key}
                  className="inline-flex items-center gap-1.5 text-[0.62rem] tracking-[0.24em] uppercase text-[var(--mm-cream)]/75 border border-[var(--mm-cream)]/15 px-2.5 py-1"
                >
                  <Icon size={10} strokeWidth={1.8} /> {label}
                </span>
              ))}
            </div>
          )}

          <p className="text-[var(--mm-cream)]/70 leading-relaxed font-light">
            {item.description}
          </p>

          {/* Special instructions */}
          <label className="block mt-6 mb-2 section-label">Anything for mama?</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value.slice(0, 140))}
            placeholder="e.g. extra spicy, no onions, allergy notes"
            rows={2}
            data-testid="dish-modal-notes"
            className="w-full bg-[var(--mm-bg-alt)]/60 border border-[var(--mm-cream)]/12 focus:border-[var(--mm-accent)] outline-none px-3 py-2.5 text-sm text-[var(--mm-cream)] placeholder:text-[var(--mm-cream)]/35 resize-none"
          />
          <p className="text-[10px] text-[var(--mm-cream)]/35 mt-1 text-right">{notes.length}/140</p>

          {/* Quantity + Add */}
          <div className="mt-6 flex items-center gap-4">
            <div className="flex items-center border border-[var(--mm-cream)]/15">
              <button
                type="button"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="w-10 h-10 flex items-center justify-center text-[var(--mm-cream)] hover:text-[var(--mm-accent)]"
                aria-label="Decrease quantity"
                data-testid="dish-modal-qty-minus"
              >
                <Minus size={14} />
              </button>
              <span className="w-10 text-center font-medium text-[var(--mm-cream)]" data-testid="dish-modal-qty">
                {qty}
              </span>
              <button
                type="button"
                onClick={() => setQty((q) => Math.min(20, q + 1))}
                className="w-10 h-10 flex items-center justify-center text-[var(--mm-cream)] hover:text-[var(--mm-accent)]"
                aria-label="Increase quantity"
                data-testid="dish-modal-qty-plus"
              >
                <Plus size={14} />
              </button>
            </div>

            <button
              type="button"
              onClick={handleAdd}
              disabled={!canAdd}
              className="flex-1 btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="dish-modal-add"
            >
              <ShoppingBag size={14} />
              {item.sold_out
                ? "Sold out"
                : `Add · $${(price * qty).toFixed(2)}`}
            </button>
          </div>

          {/* Reviews — the social-proof lever right where the decision is made. */}
          {lookup_key && (
            <DishReviewsSection lookupKey={lookup_key} onChange={refreshRatings} />
          )}
        </div>
      </div>
    </div>
  );

  return typeof document !== "undefined"
    ? createPortal(modal, document.body)
    : modal;
}
